import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';

import type { DeltaResponse, RosterContractEntry } from '../../api';
import {
  DataTable,
  type Column,
  type ColumnFilterOption,
  type ColumnFiltersState,
  type SortingState,
} from '../../components';
import { formatCpf, formatCurrency, formatDate, formatInteger, stripCpf } from '../../format';
import { staffCompanyPath } from '../../routes/paths';
import { ACTION_LABELS, primaryScheduledChange } from '../shared/roster';
import { SEGURADOS_COPY } from './segurados';
import { SCHEDULED_FILTER_OPTIONS, SEGURADOS_COLUMNS, seguradoSortValue } from './seguradosFilters';

/**
 * One contract a person is under. A company's population (#416) leaves the
 * company implicit; a group's names it on every entry.
 */
export type SeguradoEntry = RosterContractEntry & { readonly company_id?: string };

/** One person of a population read, company or group: one row per CPF. */
export interface SeguradoRow {
  readonly full_name: string;
  readonly cpf: string;
  readonly birth_date: string;
  readonly contracts: readonly SeguradoEntry[];
}

export interface SeguradosTableProps {
  /** The table's `id` for remembering hidden columns: one on company pages, one on group pages. */
  readonly id: string;
  readonly caption: string;
  /**
   * The page on screen, already filtered and sorted by the panel from the
   * state below (`seguradosFilters`): the table filters and sorts nothing
   * itself, since the panel pages what comes out.
   */
  readonly people: readonly SeguradoRow[];
  /** The entries a row speaks for. All of them unless a filter narrows the contracts. */
  readonly entriesInView?: (person: SeguradoRow) => readonly SeguradoEntry[];
  /** What the filter row holds, and the header sorted by. Never in the address. */
  readonly columnFilters: ColumnFiltersState;
  readonly onColumnFiltersChange: (filters: ColumnFiltersState) => void;
  readonly sorting: SortingState;
  readonly onSortingChange: (sorting: SortingState) => void;
  /** The choices of the Contratos filter (`contractsFilterOptions`). */
  readonly contractOptions: readonly ColumnFilterOption[];
  /** The staff person page (#418) for a row, opened from one of its entries. */
  readonly personPath: (entry: SeguradoEntry) => string;
  /** One contract as the row's tag names it -- its kind first. */
  readonly contractLabel: (entry: SeguradoEntry) => string;
  /**
   * Changes asked for and not yet happened, by bare CPF. When given, the
   * table carries the portal's "Movimentação agendada" column and its filter.
   */
  readonly scheduled?: ReadonlyMap<string, readonly DeltaResponse[]>;
  /**
   * The names of the companies the entries belong to, by id. When given -- a
   * population spanning companies -- the table carries an Empresa column,
   * filtered by a choice of these companies in this order.
   */
  readonly companies?: ReadonlyMap<string, string>;
  readonly noMatchesHint: string;
}

/**
 * The Segurados table, shared by the staff company page (#417) and the staff
 * group page (#419) so that the two lists cannot drift apart: a column added
 * here reaches both.
 *
 * Read-only. The name opens the person page, addressed by the opaque id of
 * the row's first entry, never by the CPF.
 *
 * Staff narrow and sort column by column (#441): Nome and CPF take text,
 * Contratos, Empresa and Movimentação agendada a choice, and the person's own
 * values and the per-contract dates and salary sort from their header. The
 * table is `manual` -- it holds that state and reports it, and the panel
 * applies it with `seguradosFilters` before paging -- because a contract filter
 * changes what a row shows as well as which rows stay.
 */
export function SeguradosTable({
  id,
  caption,
  people,
  entriesInView = allEntries,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  onSortingChange,
  contractOptions,
  personPath,
  contractLabel,
  scheduled,
  companies,
  noMatchesHint,
}: SeguradosTableProps) {
  return (
    <DataTable
      id={id}
      caption={caption}
      columns={columnsFor({
        entriesInView,
        contractOptions,
        personPath,
        contractLabel,
        scheduled,
        companies,
      })}
      rows={people}
      rowKey={(person) => person.cpf}
      noMatchTitle={SEGURADOS_COPY.noMatches}
      noMatchDescription={noMatchesHint}
      manual
      columnFilters={columnFilters}
      onColumnFiltersChange={onColumnFiltersChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
}

function allEntries(person: SeguradoRow): readonly SeguradoEntry[] {
  return person.contracts;
}

/**
 * One value per entry, collapsed to one when they agree.
 *
 * The salary, the coverage dates and the company belong to a contract, not to
 * the person, and two contracts can disagree. When they do, each line lines up
 * with the contract's tag in the Contratos column, in the same order -- one
 * value chosen silently would be the other contract's answer read out as this
 * one's.
 */
function perEntry(
  entries: readonly SeguradoEntry[],
  value: (entry: SeguradoEntry) => string,
  show: (entry: SeguradoEntry) => ReactNode = value,
): ReactNode {
  const values = entries.map(value);
  const first = entries[0];
  if (first === undefined) {
    return '—';
  }
  if (values.every((each) => each === values[0])) {
    return show(first);
  }
  return entries.map((entry, index) => (
    <Fragment key={entry.contract_id}>
      {index > 0 && <br />}
      {show(entry)}
    </Fragment>
  ));
}

function columnsFor({
  entriesInView,
  contractOptions,
  personPath,
  contractLabel,
  scheduled,
  companies,
}: Required<
  Pick<SeguradosTableProps, 'entriesInView' | 'contractOptions' | 'personPath' | 'contractLabel'>
> &
  Pick<SeguradosTableProps, 'scheduled' | 'companies'>): readonly Column<SeguradoRow>[] {
  /** A column sorted from its header, by the value `seguradosFilters` sorts on. */
  const sortedBy = (key: string) => ({
    key,
    accessor: (person: SeguradoRow) => seguradoSortValue(person, key, entriesInView),
    sortable: true,
  });
  const columns: Column<SeguradoRow>[] = [
    {
      ...sortedBy(SEGURADOS_COLUMNS.name),
      header: SEGURADOS_COPY.nameHeader,
      filter: { kind: 'text', placeholder: SEGURADOS_COPY.nameFilterPlaceholder },
      render: (person) => {
        const row = person.contracts[0];
        return row === undefined ? (
          person.full_name
        ) : (
          <Link to={personPath(row)}>{person.full_name}</Link>
        );
      },
    },
    {
      ...sortedBy(SEGURADOS_COLUMNS.cpf),
      header: SEGURADOS_COPY.cpfHeader,
      filter: { kind: 'text', placeholder: SEGURADOS_COPY.cpfFilterPlaceholder },
      render: (person) => <code>{formatCpf(person.cpf)}</code>,
    },
    {
      ...sortedBy(SEGURADOS_COLUMNS.birthDate),
      header: SEGURADOS_COPY.birthDateHeader,
      align: 'end',
      render: (person) => formatDate(person.birth_date),
    },
  ];

  if (companies !== undefined) {
    columns.push({
      key: SEGURADOS_COLUMNS.company,
      header: SEGURADOS_COPY.companyHeader,
      filter: {
        kind: 'select',
        options: [...companies].map(([value, label]) => ({ value, label })),
      },
      render: (person) =>
        perEntry(
          entriesInView(person),
          (entry) => entry.company_id ?? '',
          (entry) =>
            entry.company_id === undefined ? (
              '—'
            ) : (
              <Link to={staffCompanyPath(entry.company_id)}>
                {companies.get(entry.company_id) ?? SEGURADOS_COPY.unknownCompany}
              </Link>
            ),
        ),
    });
  }

  columns.push(
    {
      ...sortedBy(SEGURADOS_COLUMNS.salary),
      header: SEGURADOS_COPY.salaryHeader,
      align: 'end',
      render: (person) => perEntry(entriesInView(person), (entry) => formatCurrency(entry.salary)),
    },
    {
      ...sortedBy(SEGURADOS_COLUMNS.coverageFrom),
      header: SEGURADOS_COPY.coverageFromHeader,
      align: 'end',
      render: (person) =>
        perEntry(entriesInView(person), (entry) => formatDate(entry.coverage_from)),
    },
    {
      ...sortedBy(SEGURADOS_COLUMNS.coverageTo),
      header: SEGURADOS_COPY.coverageToHeader,
      align: 'end',
      render: (person) => perEntry(entriesInView(person), (entry) => formatDate(entry.coverage_to)),
    },
  );

  if (scheduled !== undefined) {
    columns.push({
      key: SEGURADOS_COLUMNS.scheduled,
      header: SEGURADOS_COPY.scheduledHeader,
      filter: { kind: 'select', options: SCHEDULED_FILTER_OPTIONS },
      render: (person) => {
        const changes = scheduled.get(stripCpf(person.cpf)) ?? [];
        const primary = primaryScheduledChange(changes);
        if (primary === null) {
          return <span className="table__muted">—</span>;
        }
        // The portal's tag, word for word: both halves off the same delta.
        return (
          <>
            <span className="tag tag--scheduled">
              {ACTION_LABELS[primary.action]} em {formatDate(primary.effective_date)}
            </span>
            {changes.length > 1 && (
              <span className="table__muted">
                {' '}
                e mais {formatInteger(changes.length - 1)} agendada(s)
              </span>
            )}
          </>
        );
      },
    });
  }

  columns.push({
    key: SEGURADOS_COLUMNS.contracts,
    header: SEGURADOS_COPY.contractsHeader,
    filter: { kind: 'select', options: contractOptions },
    // The entries in view, in the order the per-contract values above stack.
    render: (person) =>
      entriesInView(person).map((entry, index) => (
        <Fragment key={entry.contract_id}>
          {index > 0 && ' '}
          <span className="badge">{contractLabel(entry)}</span>
        </Fragment>
      )),
  });

  return columns;
}
